export interface NewsIndexItem {
  meta: {
    id: number;
    title: string;
    type: string;
    detail_url: string;
    html_url: string | null;
    slug: string;
    show_in_menus: boolean;
    seo_title: string;
    search_description: string;
    first_published_at: string | null;
    alias_of: number | null;
    parent: any;
    locale: string;
    last_published_at: string | null;
  };
  subtitle: string;
  introduction: string;
  image: any;
  content: string;
  tags: string[];
  edit_url: string;
  date_published: string;
}

export interface NewsItem {
  meta: {
    id: number;
    title: string;
    type: string;
    detail_url: string;
    html_url: string | null;
    slug: string;
    show_in_menus: boolean;
    seo_title: string;
    search_description: string;
    alias_of: number | null;
    parent: NewsIndexItem | null;
    locale: string;
    last_published_at: string;
    first_published_at: string;
    category: string;
  };
  subtitle: string;
  introduction: string;
  image: any;
  content: string;
  tags: string[];
  edit_url: string;
  date_published: string;
  unread?: boolean;
  notification_id?: number;
}

export const useNewsStore = defineStore('news', () => {
  const newsItems = ref<NewsItem[]>([]);
  const newsLoading = ref(false);
  const offset = ref(0);
  const limit = ref(10);
  const totalNewsItems = ref(0);
  const newsPage = ref<NewsItem | null>(null);
  const newsIndexList = ref<NewsIndexItem[]>([]);
  const newsIndexListLoading = ref(false);

  const loadRecentNews = async (parentId: number | null = null) => {
    if (newsLoading.value) return;
    newsLoading.value = true;
    try {
      const response = await apiGetNews(parentId, offset.value, limit.value);
      newsItems.value = response.data.results;
      totalNewsItems.value = response.data.total;
    } catch (err) {
      console.error('Error while fetching news', err);
      newsItems.value = [];
      throw new Error('An error occurred while fetching news.');
    } finally {
      newsLoading.value = false;
    }
  };

  const getNewsItem = async (newsPageId: number) => {
    try {
      const response = await apiGetNewsItem(newsPageId);
      newsPage.value = response.data;
      return response.data;
    } catch (e) {
      console.error(e);
      throw new Error('An error occurred while fetching the news item.');
    }
  };

  const refreshNews = async () => {
    await loadRecentNews();
  };

  const getNewsIndexList = async () => {
    if (newsIndexListLoading.value) return newsIndexList.value;
    newsIndexListLoading.value = true;
    try {
      const response = await apiGetNewsIndexList();
      const items = response.data.results;
      newsIndexList.value = items;
      return newsIndexList.value;
    } catch (e) {
      console.error(e);
      throw new Error('An error occurred while fetching the news index categories.');
    } finally {
      newsIndexListLoading.value = false;
    }
  };

  return {
    newsItems,
    newsLoading,
    loadRecentNews,
    refreshNews,
    getNewsItem,
    offset,
    limit,
    totalNewsItems,
    newsPage,
    newsIndexList,
    newsIndexListLoading,
    getNewsIndexList,
  };
});
